/* inferBsMeta — pure, fail-loud BS chart-of-accounts classifier for onboarding Step 4.
 *
 * Generalizes the one-shot `server/migrate-ae-model-bs-meta.js` (a hardcoded
 * per-id lookup for cid=3) into a tenant-agnostic inference, so a NEW tenant
 * exits onboarding with a fully classified `bs_actuals_meta` instead of the
 * bare {label, category, gl_name, gl_category} that broke the sweep.
 *
 * SHAPE mirrors `server/migrate-ae-model-bs-assumptions.js`'s infer():
 *   - PURE: no I/O, no DB, no hardcoded tenant line-ids.
 *   - KEYWORD-DRIVEN: keys on the operator's answer + label/gl_name text.
 *   - ★ FAIL-LOUD: throws on an unclassifiable leaf. It NEVER silently defaults
 *     or guesses — a wrong-but-plausible role produces a silently wrong model
 *     (the exact class of bug this module exists to prevent).
 *
 * DIVISION OF LABOUR — infer() proposes, the operator disposes:
 *   - The operator answers only what is genuinely un-inferable: which cash
 *     account is the SWEEP plug, and which current assets ROLL INTO the Total
 *     Cash rollup vs stand SEPARATE. (Phase 0 proved name-detection is
 *     unreliable in BOTH directions — it misses real bank accounts and
 *     false-positives on the de-facto-AR account.)
 *   - Everything else (AR / prepaid / accrued / AP / fixed / intangible /
 *     equity roles, display_order, cfs_id) is inferred here.
 *   - SUPERSEDED IN PART (kept as written above): since 2026-09-24 every liability and
 *     equity line carries a Step-4a TYPE answer, and since 2026-10-09 (A8) an unanswered
 *     one is REFUSED rather than inferred from its name. Fixed / intangible lines need a
 *     gross-vs-contra answer (B5 s5). The name tests remain as the visible suggestion.
 */

// ── Category normalization ───────────────────────────────────────────────────
// ★ The Step-3 GL-mapping categories are NOT the engine's category vocabulary.
// The engine buckets leaves into exactly five (buildBSLines.js `byCategory`):
//   current_assets | non_current_assets | current_liabilities |
//   non_current_liabilities | equity
// AdminPage's BS_CAT_TO_SNAKE emits 'fixed_assets' / 'intangible_assets' /
// 'lt_liabilities', NONE of which are engine categories — leaves carrying them
// are silently DROPPED from the BS. Session A hand-corrected cid=3's 7 asset
// rows to category='non_current_assets' + subcategory='fixed_assets'; the
// LT-liability case never surfaced only because AE Model has none.
// This map is the generic correction.
// ★ This map, the Step-3 dropdown list and the display->snake map were three separate
// hardcoded literals that had to agree by hand, plus a fourth spelling of the
// fixed/intangible split in the asset branch below. They are now ONE vocabulary —
// utils/bsCategoryVocab — which also owns the SUB-TYPE and its two role pairs. Key order
// there is the order this module's fail-loud message lists.
import { BS_SNAKE_TO_ENGINE_CATEGORY, BS_SNAKE_TO_SUBTYPE, BS_ASSET_ROLES } from './bsCategoryVocab';

// Roles that get NO cfs_id — they are the cash reconciliation, the D&A add-back,
// the net-income line, or a module-driven DR line whose chg_dr_* is generated
// from revenue_module_mappings. Mirrors CFS_EXCLUDED_ROLES in lineCategories.js.
const NO_CFS_ID_ROLES = new Set([
  'cash', 'clearing', 'sweep_target',
  'accumulated_depreciation', 'accumulated_amortization',
  'rolling_retained_earnings', 'ytd_net_income',
  'deferred_revenue',
]);

// CFS activity-line prefix per engine category (mirrors buildCFSLines.classifyForCFS).
const CFS_PREFIX = {
  current_assets:          'chg',
  current_liabilities:     'chg',
  non_current_assets:      'inv',
  non_current_liabilities: 'fin',
  equity:                  'fin',
};
// A role whose cash-flow section is NOT its category's. Short-term debt is a current liability
// on the balance sheet and a FINANCING flow on the cash flow (buildCFSLines.classifyForCFS
// makes the same exception). One entry; every other role takes its category's prefix.
const CFS_PREFIX_BY_ROLE = {
  short_term_debt: 'fin',
};

// display_order bands, reproducing Session A's 100/110/…/990 scheme. Within a
// band, leaves are numbered in encounter order (see assignDisplayOrders).
const BANDS = [
  { base: 100, match: m => m.role === 'sweep_target' },
  { base: 110, match: m => m.role === 'cash' || m.role === 'clearing' },
  { base: 190, match: m => m.category === 'current_assets' && m.role === 'accounts_receivable' },
  { base: 230, match: m => m.role === 'prepaid' },
  { base: 300, match: m => m.role === 'fixed_asset' },
  { base: 390, match: m => m.role === 'accumulated_depreciation' },
  { base: 400, match: m => m.role === 'intangible_asset' },
  { base: 490, match: m => m.role === 'accumulated_amortization' },
  { base: 500, match: m => m.role === 'accounts_payable' && m.subcategory === 'accounts_payable' },
  { base: 510, match: m => m.role === 'accounts_payable' && m.subcategory === 'credit_card' },
  { base: 600, match: m => m.role === 'accrued' },
  { base: 680, match: m => m.role === 'deferred_revenue' },
  { base: 690, match: m => m.role === 'short_term_debt' },
  { base: 700, match: m => m.category === 'non_current_liabilities' },
  { base: 800, match: m => m.role === 'contributed_capital' },
  { base: 980, match: m => m.role === 'rolling_retained_earnings' },
  { base: 990, match: m => m.role === 'ytd_net_income' },
];

const txt = leaf => `${leaf.label || ''} ${leaf.gl_name || ''}`.toLowerCase();

/* Sets the Step-4 panel's DEFAULT for a current-asset row (Separate vs Total Cash)
 * and drives its advisory note. It never filters, hides, or locks a row — the
 * operator sees every account and can set any of the three options.
 *
 * A cash-positive token WINS over a non-cash one: "Emburse Prepaid Debit Card" is a
 * real bank/cash account whose name happens to contain "Prepaid" — without this, it
 * would default OUT of the rollup and understate Total Cash. (Ordering matters:
 * "Prepaid Expenses" carries no cash token and correctly stays Separate.)
 *
 * This is a heuristic and it is ALLOWED to be wrong — it only moves a radio button.
 * The operator's real check is the live "Total Cash at opening" total against their
 * actual bank balance. */
const CASH_POSITIVE = /\b(bank|checking|savings|money\s*market|debit\s*card|card)\b/i;
const NON_CASH = /receivable|prepaid|deposit|accrued|inventory/i;

export function looksNonCash(leaf) {
  const t = txt(leaf);
  if (CASH_POSITIVE.test(t)) return false;
  return NON_CASH.test(t);
}

/* Role/subcategory for a CURRENT ASSET the operator marked 'separate'.
 * The operator's answer says only "this does not belong in Total Cash"; WHICH
 * kind of separate line it is, is inferable — and getting it right matters:
 *   AR + accounts_receivable  → bs_assumptions driver = DSO
 *   prepaid                   → driver = module_link (drives the prepaid schedule)
 *   AR + other_current_asset  → driver = constant (the de-facto-AR escape hatch)
 * A blanket 'accounts_receivable/other_current_asset' would silently demote the
 * real A/R line to a constant and unhook the prepaid schedule. */
/* ★★ THE THREE NAME TESTS — ONE OWNER, and now EXPORTED so the Step-4a panel can ask the
 * same question the import will answer. Returns the matched classification, or NULL when
 * no test matches (which is the case the operator is asked about).
 *
 * ⛔ THE PANEL MUST CALL THIS, NEVER COPY THE PATTERNS. A second copy of these three
 * regexes is a second classification, and the whole prepaid arc was spent removing exactly
 * that (§6). `looksNonCash` is the precedent: an exported predicate from this module that
 * the panel already imports for its default.
 *
 * It reads only `label` and `gl_name` (via txt), and a Step-4a option object carries both —
 * measured in diffs/bs-catchall-phase0.txt §3a, which is what makes SHAPE A expressible
 * without new plumbing. */
export function separateCurrentAssetNameMatch(leaf) {
  const t = txt(leaf);
  if (/prepaid/.test(t))               return { role: 'prepaid', subcategory: 'prepaid', module_link_ref: 'prepaid_balance' };
  if (/other\s+receivable/.test(t))    return { role: 'accounts_receivable', subcategory: 'other_receivable' };
  if (/accounts?\s+receivable|\ba\/?r\b/.test(t))
                                       return { role: 'accounts_receivable', subcategory: 'accounts_receivable' };
  return null;
}

/* The Step-4a answer vocabulary for a current-asset leaf the operator pulled OUT of the
 * rollup. 'separate' is the bare parent answer; the two widened values carry the
 * sub-answer for a row no name test recognises.
 *
 * ★ ONE CHANNEL, NOT TWO. The classification map holds ONE value per bsKey, and s5 was
 * emphatic that the asset answers "join the SAME map and the SAME key space — ONE
 * classification channel, not two (§6)". So the sub-answer WIDENS the value rather than
 * arriving on a second map. */
export const SEPARATE_ANSWERS = {
  base: 'separate', receivable: 'separate_receivable', constant: 'separate_constant',
  // Step-4a type picker (2026-09-24): every Separate row now carries its type, so a row the
  // name tests DO recognise can be overridden too. Two more values for the two kinds the
  // tests used to decide with no way to change.
  prepaid: 'separate_prepaid', otherReceivable: 'separate_other_receivable',
};
export const isSeparateAnswer = (a) => Object.values(SEPARATE_ANSWERS).includes(a);

/* ★★ THE SILENT CATCH-ALL IS GONE (Build B). It used to be:
 *       return { role: 'accounts_receivable', subcategory: 'other_current_asset' };
 * — an unconditional final line with no fail-loud, so a current asset whose name matched
 * none of the three tests was silently parked as a CONSTANT. That is not cosmetic: A/R vs
 * other_current_asset selects `dso days=30 base=total_revenue` vs `constant`
 * (inferBsAssumption), so a tenant whose receivables line is called "Trade Debtors" or
 * "Customer Balances" got a FROZEN A/R line — silently, and WITH THE BALANCE SHEET STILL
 * BALANCING, which makes it harder to notice than the /accum/i guess s5 retired (that one
 * at least broke the check). MEASURED: 12 of 24 separate rows across the four tenants
 * reached it (diffs/bs-phase0.txt).
 *
 * ★ SHAPE A — THE BACKSTOP, NOT A BLANKET QUESTION. A name-test match STILL WINS and still
 * auto-classifies, exactly as before; the operator is asked ONLY on the else-case. That
 * keeps prepaid / other-receivable / A/R rows silent and unchanged.
 * ★ AND AN UNANSWERED ELSE-CASE THROWS, like every other unclassifiable leaf here. */
function resolveSeparateCurrentAsset(leaf, answer) {
  // THE OPERATOR'S ANSWER COMES FIRST (2026-09-24). A specific type from the Step-4a picker
  // wins over the name test -- the test only SUGGESTS. It used to be the other way round,
  // so "Prepaid Expenses" could never be told it was something else. A bare 'separate'
  // (no type chosen) still falls back to the name test, and past it still THROWS.
  const chosen = bsTypeOption('separate', answer);
  if (chosen) return chosen;
  const named = separateCurrentAssetNameMatch(leaf);
  if (named) return named;
  throw new Error(
    `[inferBsMeta] "${leaf.label}" (${leaf.bsKey}) was pulled out of the Total Cash rollup, ` +
    `but its name matches none of the known current-asset patterns, so which KIND of ` +
    `current asset it is cannot be inferred. Choose its type on Step 4a (a receivable is ` +
    `forecast from DSO; an other current asset is carried forward). ` +
    `REFUSING to guess — the two drive different forecasts and the balance sheet balances ` +
    `either way, so a wrong guess is invisible.`);
}

function inferCurrentLiability(leaf) {
  const t = txt(leaf);
  // Order matters. "Deferred Income Tax" contains 'deferred' but is NOT deferred
  // revenue; "Accrued Expense Payable" contains 'payable' but is NOT A/P.
  //
  // "Contract Liabilities" is the ASC 606 name for deferred revenue. A California sales-tax
  // account is "Board of Equalization Payable", which names no tax word.
  //
  // THE TRADE A/P TEST IS NARROW ON PURPOSE. It used to be /payable|\ba\/?p\b/, so ANY
  // "... Payable" became trade A/P and was forecast as 30 days of vendor spend -- "Loan
  // Payable" and "Board of Equalization Payable" were misfiled silently, with the balance
  // sheet still balancing (diffs/bs-classifier-qbo-phase0.txt: a 500,000 loan fell to
  // 286,968.58 by December). Now only an accounts / trade payable is suggested as A/P; a
  // loan, a note, a line of credit is suggested as short-term debt; any other "... Payable"
  // matches nothing and the operator chooses its type on Step 4a.
  if (/deferred\s+revenue|unearned\s+revenue|contract\s+liabilit/.test(t)) return { role: 'deferred_revenue', subcategory: 'deferred_revenue' };
  if (/credit\s*card/.test(t))                         return { role: 'accounts_payable', subcategory: 'credit_card' };
  if (/payroll/.test(t))                               return { role: 'accrued', subcategory: 'payroll' };
  if (/\btax(es)?\b|board\s+of\s+equalization/.test(t)) return { role: 'accrued', subcategory: 'tax' };
  if (/accrued/.test(t))                               return { role: 'accrued', subcategory: 'accrued' };
  if (/\bloans?\b|\bnotes?\s+payable|line\s+of\s+credit|credit\s+line|\bloc\b|\bborrowings?\b/.test(t))
                                                       return { role: 'short_term_debt', subcategory: 'short_term_debt' };
  if (/accounts?\s+payable|trade\s+payable|\ba\/?p\b/.test(t)) return { role: 'accounts_payable', subcategory: 'accounts_payable' };
  return null; // fail-loud
}

function inferEquity(leaf) {
  const t = txt(leaf);
  // Order matters. "Preferred Stock Series A Issuance Costs" contains BOTH
  // 'preferred' and 'issuance' — issuance wins (Session A).
  if (/retained\s+earnings/.test(t)) return { role: 'rolling_retained_earnings', subcategory: 'retained_earnings' };
  if (/net\s+income/.test(t))        return { role: 'ytd_net_income',            subcategory: 'retained_earnings' };
  if (/issuance\s*cost/.test(t))     return { role: 'contributed_capital',       subcategory: 'issuance_costs' };
  if (/treasury/.test(t))            return { role: 'contributed_capital',       subcategory: 'treasury' };
  if (/preferred/.test(t))           return { role: 'contributed_capital',       subcategory: 'preferred_stock' };
  if (/common/.test(t))              return { role: 'contributed_capital',       subcategory: 'common_stock' };
  // Additional paid-in capital, QuickBooks' "Opening Balance Equity", and an owner's or a
  // partner's equity account: all contributed capital, held constant. They come after the
  // tests above, so "Preferred Stock APIC" stays preferred and "Common Stock APIC" common.
  if (/paid[\s-]*in|\bapic\b/.test(t))  return { role: 'contributed_capital',       subcategory: 'common_stock' };
  if (/opening\s+balance\s+equity/.test(t)) return { role: 'contributed_capital',  subcategory: 'common_stock' };
  if (/\bowner|\bpartner/.test(t))      return { role: 'contributed_capital',       subcategory: 'common_stock' };
  if (/safe|convertible|note/.test(t)) return { role: 'contributed_capital',     subcategory: 'common_stock' };
  return null; // fail-loud
}

/* THE STEP-4a TYPE VOCABULARY -- ONE OWNER (2026-09-24).
 *
 * Every Separate current asset, every current liability and every equity line gets a TYPE
 * PICKER on Step 4a. The options are the roles and subcategories this module already
 * produces (plus short_term_debt); each option's forecast driver is NOT restated here, it
 * is read from inferBsAssumption (bsTypeDriver), so the picker cannot describe a driver the
 * import would not use.
 *
 * The answer rides the SAME { [bsKey]: answer } classification map the cash and asset
 * panels already use -- one channel, not two. A name test only SUGGESTS: the panel shows
 * the suggestion as "suggested -- confirm or change", and inferBsMetaLeaf consumes the
 * operator's answer -- and, for a liability or equity line, refuses a row with none (A8,
 * 2026-10-09). Long-term liabilities have one type (debt) and no picker.
 * Formula lines (retained earnings, current-year net income) are offered so an equity line
 * no test recognises ("Accumulated Deficit") can still be one. */
export const BS_TYPE_OPTIONS = {
  separate: [
    { answer: SEPARATE_ANSWERS.receivable,      label: 'Accounts receivable',            role: 'accounts_receivable', subcategory: 'accounts_receivable' },
    { answer: SEPARATE_ANSWERS.otherReceivable, label: 'Other receivable',               role: 'accounts_receivable', subcategory: 'other_receivable' },
    { answer: SEPARATE_ANSWERS.prepaid,         label: 'Prepaid expenses',               role: 'prepaid',             subcategory: 'prepaid', module_link_ref: 'prepaid_balance' },
    { answer: SEPARATE_ANSWERS.constant,        label: 'Other current asset',            role: 'accounts_receivable', subcategory: 'other_current_asset' },
  ],
  current_liabilities: [
    { answer: 'liab_trade_payable',    label: 'Trade accounts payable',                  role: 'accounts_payable', subcategory: 'accounts_payable' },
    { answer: 'liab_credit_card',      label: 'Credit card',                             role: 'accounts_payable', subcategory: 'credit_card' },
    { answer: 'liab_accrued',          label: 'Accrued liability',                       role: 'accrued',          subcategory: 'accrued' },
    { answer: 'liab_payroll',          label: 'Accrued payroll',                         role: 'accrued',          subcategory: 'payroll' },
    { answer: 'liab_tax',              label: 'Tax payable',                             role: 'accrued',          subcategory: 'tax' },
    { answer: 'liab_deferred_revenue', label: 'Deferred revenue',                        role: 'deferred_revenue', subcategory: 'deferred_revenue' },
    { answer: 'liab_short_term_debt',  label: 'Short-term debt (loan, line of credit)',  role: 'short_term_debt',  subcategory: 'short_term_debt' },
  ],
  equity: [
    { answer: 'eq_common',            label: 'Common stock, paid-in capital or owner equity', role: 'contributed_capital',       subcategory: 'common_stock' },
    { answer: 'eq_preferred',         label: 'Preferred stock',                          role: 'contributed_capital',       subcategory: 'preferred_stock' },
    { answer: 'eq_treasury',          label: 'Treasury stock',                           role: 'contributed_capital',       subcategory: 'treasury' },
    { answer: 'eq_issuance_costs',    label: 'Issuance costs',                           role: 'contributed_capital',       subcategory: 'issuance_costs' },
    { answer: 'eq_retained_earnings', label: 'Retained earnings (rolls forward)',        role: 'rolling_retained_earnings', subcategory: 'retained_earnings' },
    { answer: 'eq_net_income',        label: 'Net income, current year',                 role: 'ytd_net_income',            subcategory: 'retained_earnings' },
  ],
};

/* The picker section for an engine category: current assets are asked only when Separate. */
const TYPE_SECTION_OF = { current_assets: 'separate', current_liabilities: 'current_liabilities', equity: 'equity' };
export const bsTypeSection = (snakeCategory) => TYPE_SECTION_OF[BS_SNAKE_TO_ENGINE_CATEGORY[snakeCategory]] || null;

/* The option an answer names in a section, or null. */
function bsTypeOption(section, answer) {
  if (!answer) return null;
  const o = (BS_TYPE_OPTIONS[section] || []).find(x => x.answer === answer);
  if (!o) return null;
  return { role: o.role, subcategory: o.subcategory, ...(o.module_link_ref ? { module_link_ref: o.module_link_ref } : {}) };
}

/* The answer whose role + subcategory match -- used to read a STORED classification back
 * as a confirmed answer on re-entry, and to map a name test onto the picker. */
export function bsTypeOf(section, role, subcategory) {
  const o = (BS_TYPE_OPTIONS[section] || []).find(x => x.role === role && x.subcategory === subcategory);
  return o ? o.answer : null;
}

/* The name test's SUGGESTION for a leaf, as a picker answer, or null (the row is blank and
 * required). The leaf is { label, gl_name, category (snake) }. The panel owns no copy of the
 * patterns. (A8, 2026-10-09: for a liability or equity line this is the name tests' ONLY
 * use -- the import no longer falls back to them; a separate current asset still does,
 * SHAPE A, resolveSeparateCurrentAsset.) */
export function bsTypeSuggestion(leaf) {
  const section = bsTypeSection(leaf.category);
  const named = section === 'separate' ? separateCurrentAssetNameMatch(leaf)
    : section === 'current_liabilities' ? inferCurrentLiability(leaf)
      : section === 'equity' ? inferEquity(leaf) : null;
  return named ? bsTypeOf(section, named.role, named.subcategory) : null;
}

/* The forecast driver an answer selects, read from inferBsAssumption -- the picker shows it. */
export function bsTypeDriver(section, answer) {
  const o = bsTypeOption(section, answer);
  return o ? inferBsAssumption(o) : null;
}

/* Classify ONE leaf. `answer` is the operator's Step-4a choice for THIS leaf:
 *   current assets      → 'sweep' | 'rollup' | 'separate'
 *   non-current assets  → 'gross' | 'contra'   (B5 s5 — was a /accum/i name test)
 *   current liabilities / equity → a BS_TYPE_OPTIONS answer (A8, 2026-10-09 -- was a name
 *                       test when unanswered)
 * Throws if the leaf cannot be classified, and — for an asset, liability or equity leaf —
 * if it is UNANSWERED. An unanswered leaf must NEVER fall back to a name guess (§E1): a guess
 * displayed as a decision is indistinguishable from one the operator actually made. */
export function inferBsMetaLeaf(leaf, answer) {
  const engineCategory = BS_SNAKE_TO_ENGINE_CATEGORY[leaf.category];
  if (!engineCategory) {
    throw new Error(
      `[inferBsMeta] Cannot classify "${leaf.label}" (${leaf.bsKey}): unknown balance-sheet ` +
      `category "${leaf.category}". Expected one of ${Object.keys(BS_SNAKE_TO_ENGINE_CATEGORY).join(', ')}. ` +
      `Fix the line's category in Step 3 (GL Mapping).`
    );
  }

  let out = null;

  if (engineCategory === 'current_assets') {
    const a = answer || 'rollup';
    if (a === 'sweep') {
      out = { role: 'sweep_target', subcategory: 'sweep' };
    } else if (a === 'rollup') {
      // A payment-processor holding account is 'clearing'; a bank account is 'cash'.
      // Both fold into the total_clearing rollup identically (subcategory='clearing');
      // the role split is descriptive only.
      out = { role: /clearing/i.test(txt(leaf)) ? 'clearing' : 'cash', subcategory: 'clearing' };
    } else if (isSeparateAnswer(a)) {
      // Build B: 'separate' | 'separate_receivable' | 'separate_constant'. A name-test
      // match wins regardless of which (SHAPE A); the widened values are consulted ONLY
      // when no test matched, and a bare 'separate' there now THROWS.
      out = resolveSeparateCurrentAsset(leaf, a);
    } else {
      throw new Error(`[inferBsMeta] Unknown classification answer "${answer}" for "${leaf.label}" (${leaf.bsKey}).`);
    }
  } else if (engineCategory === 'non_current_assets') {
    // The engine has ONE asset category; fixed-vs-intangible is a SUB-TYPE, and it comes
    // from the operator's Step-3 category through the one vocabulary (§E1 — no name guess).
    // The `||` reproduces the previous else-branch exactly and is unreachable today: only
    // these two snakes resolve to 'non_current_assets'.
    const subType = BS_SNAKE_TO_SUBTYPE[leaf.category] || 'intangible_assets';
    // ★ B5 s5 — GROSS-vs-CONTRA IS NOW THE OPERATOR'S ANSWER, not a name test. It used to
    // be `/accum/i.test(txt(leaf))`, so a tenant naming the line "Reserve for Depreciation"
    // was classified a GROSS asset — and the two answers select OPPOSITE formulas
    // (accum_depr SUBTRACTS depreciation each month, fa_gross ADDS additions), so the
    // balance sheet broke by the cumulative depreciation, silently and growing. The role
    // also feeds four other consumers: the CFS fa_writeoff derivation, the CFS exclusion
    // list, computeDA's BS-line filter, and the Step-8 asset BS-line options.
    // The answer arrives on the SAME classification channel the current-asset branch has
    // always used — keyed by bsKey, from the Step-4a panel.
    const roles = BS_ASSET_ROLES[subType];
    if (answer === 'contra')     out = { role: roles.contra, subcategory: subType };
    else if (answer === 'gross') out = { role: roles.gross,  subcategory: subType };
    else {
      // ⛔ NO FALLBACK. Not to /accum/i, not to a default. An unanswered asset leaf is an
      // unanswered question, and the wizard blocks on it (AdminPage Step 4a).
      throw new Error(
        `[inferBsMeta] "${leaf.label}" (${leaf.bsKey}) is a ${subType === 'intangible_assets' ? 'intangible' : 'fixed'}-asset ` +
        `line with no gross-vs-contra answer. Choose "the asset itself" or the accumulated ` +
        `contra line for it on Step 4a before importing.`
      );
    }
  } else if (engineCategory === 'current_liabilities' || engineCategory === 'equity') {
    // THE OPERATOR'S ANSWER, AND ONLY IT (A8, Brian 2026-10-09). Until 10-09 an unanswered
    // row fell back to the name test here (inferCurrentLiability / inferEquity), silently --
    // the stored role it wrote drives the forecast driver, the cash-flow id and, for
    // 'accrued', the bi-weekly accrued-payroll account picker. The Step-4a page always sends
    // an answer (the operator's, or the marked suggestion the page checkbox accepted), so
    // the fallback only ever served a caller that omitted one. Now that caller is refused.
    // The name tests stay: they power the VISIBLE suggestion (bsTypeSuggestion).
    // An answer from another vocabulary is refused rather than ignored.
    const section = TYPE_SECTION_OF[engineCategory];
    if (answer === undefined || answer === null || answer === '') {
      throw new Error(
        `[inferBsMeta] REFUSING to guess: "${leaf.label}" (${leaf.bsKey}) is ` +
        `${engineCategory === 'equity' ? 'an equity line' : 'a current liability'} with no type — ` +
        `choose its type on Step 4a before importing. A type is never taken from the line's name.`);
    }
    out = bsTypeOption(section, answer);
    if (!out) throw new Error(`[inferBsMeta] Unknown ${section} type "${answer}" for "${leaf.label}" (${leaf.bsKey}).`);
  } else if (engineCategory === 'non_current_liabilities') {
    out = { role: 'debt', subcategory: 'debt' };
  }

  if (!out) {
    throw new Error(
      `[inferBsMeta] Cannot classify "${leaf.label}" (${leaf.bsKey}) in ${engineCategory}: ` +
      `no rule matched and no type was chosen. Choose its type on Step 4a before importing. ` +
      `REFUSING to guess — a wrong role produces a silently wrong model.`
    );
  }

  const cfs_id = NO_CFS_ID_ROLES.has(out.role) ? null
    : `${CFS_PREFIX_BY_ROLE[out.role] || CFS_PREFIX[engineCategory]}_${leaf.bsKey}`;

  return {
    category: engineCategory,
    subcategory: out.subcategory,
    role: out.role,
    cfs_id,
    cfs_label: null,
    ...(out.module_link_ref ? { module_link_ref: out.module_link_ref } : {}),
  };
}

/* Second pass: assign display_order from the role/category band + encounter order.
 * Within a band, leaves are numbered in the tenant's own GL sort order (step 1, so a
 * band can never bleed into the next). display_order is presentation-only — it sorts
 * rows in buildBSLines / buildCFSLines. */
function assignDisplayOrders(entries) {
  const counters = new Map();
  for (const e of entries) {
    const idx = BANDS.findIndex(b => b.match(e));
    if (idx < 0) {
      throw new Error(`[inferBsMeta] No display_order band for role="${e.role}" category="${e.category}".`);
    }
    const band = BANDS[idx];
    const nextBase = idx + 1 < BANDS.length ? BANDS[idx + 1].base : Infinity;
    const n = counters.get(band.base) || 0;
    counters.set(band.base, n + 1);
    const order = band.base + n;
    // ★ Fail loud rather than silently bleed into the next section's band — an
    // overflowing band would re-order the balance sheet with no error.
    if (order >= nextBase) {
      throw new Error(
        `[inferBsMeta] display_order band ${band.base} overflowed into ${nextBase} ` +
        `(${n + 1} leaves with role="${e.role}"). Widen the band.`
      );
    }
    e.display_order = order;
  }
  return entries;
}

/* Classify the whole leaf set. `leaves` = [{bsKey, label, gl_name, gl_category, category}]
 * in stable (GL sort) order. `classification` = { [bsKey]: 'sweep'|'rollup'|'separate' }.
 * Returns { [bsKey]: {category, subcategory, role, display_order, cfs_id, cfs_label, module_link_ref?} }.
 * Throws on any unclassifiable leaf, or if the sweep target isn't exactly one. */
export function inferBsMetaAll(leaves, classification) {
  const cls = classification || {};
  const sweeps = leaves.filter(l => cls[l.bsKey] === 'sweep');
  if (sweeps.length !== 1) {
    throw new Error(
      `[inferBsMeta] Exactly one account must be designated the sweep target; got ${sweeps.length}` +
      (sweeps.length ? ` (${sweeps.map(s => s.bsKey).join(', ')})` : '') + '.'
    );
  }

  // EVERY unclassifiable leaf is named in one message (2026-09-24). It used to stop at the
  // first, so an operator fixed one line, imported again, and met the next.
  const entries = [], failed = [];
  for (const l of leaves) {
    try { entries.push({ bsKey: l.bsKey, ...inferBsMetaLeaf(l, cls[l.bsKey]) }); }
    catch (e) { failed.push(e.message); }
  }
  if (failed.length === 1) throw new Error(failed[0]);
  if (failed.length) {
    throw new Error(`[inferBsMeta] ${failed.length} balance-sheet lines cannot be classified:\n  - ` + failed.join('\n  - '));
  }
  assignDisplayOrders(entries);

  const out = {};
  for (const e of entries) {
    const { bsKey, ...rest } = e;
    out[bsKey] = rest;
  }
  return out;
}

/* The rollup members — the leaves that fold into total_clearing.sumOf, i.e. the
 * engine's operating-cash basis. Also the set summed into cfs_dec23.total_clearing. */
export function rollupMemberKeys(leaves, classification) {
  const cls = classification || {};
  return leaves.filter(l => (cls[l.bsKey] || 'rollup') === 'rollup' && l.category === 'current_assets')
               .map(l => l.bsKey);
}

/* ── bs_assumptions inference ────────────────────────────────────────────────
 * Lifted verbatim from server/migrate-ae-model-bs-assumptions.js. Keys purely on
 * role + subcategory — which Step 4 now writes — so it "falls out once roles
 * exist" (§12). Returns null for an unmatched leaf; the caller fails loud. */
export function inferBsAssumption(meta) {
  const role = meta.role;
  const sub = meta.subcategory;
  if (role === 'sweep_target')                                          return { driver: 'module_link' };
  if (role === 'clearing')                                              return { driver: 'constant', fz: true };
  if (role === 'cash')                                                  return { driver: 'constant', fz: true };
  if (role === 'prepaid')                                               return { driver: 'module_link' };
  if (role === 'accounts_receivable' && sub === 'accounts_receivable')  return { driver: 'dso', days: 30, base_is_line_id: 'total_revenue' };
  if (role === 'accounts_receivable' && sub === 'other_receivable')     return { driver: 'constant' };
  if (role === 'accounts_receivable' && sub === 'other_current_asset')  return { driver: 'constant' };
  if (role === 'accounts_payable' && sub === 'accounts_payable')        return { driver: 'dpo', days: 30, base_is_line_id: 'vendor_opex' };
  if (role === 'accounts_payable' && sub === 'credit_card')             return { driver: 'dpo', days: 30, base_is_line_id: 'vendor_opex' };
  if (role === 'accrued')                                               return { driver: 'constant' };
  if (role === 'deferred_revenue')                                      return { driver: 'module_link' };
  if (role === 'debt')                                                  return { driver: 'constant' };
  if (role === 'short_term_debt')                                       return { driver: 'constant' };
  if (role === 'fixed_asset')                                           return { driver: 'formula', formula: 'fa_gross' };
  if (role === 'accumulated_depreciation')                              return { driver: 'formula', formula: 'accum_depr' };
  if (role === 'intangible_asset')                                      return { driver: 'formula', formula: 'ia_gross' };
  if (role === 'accumulated_amortization')                              return { driver: 'formula', formula: 'accum_amort' };
  if (role === 'contributed_capital')                                   return { driver: 'constant' };
  if (role === 'rolling_retained_earnings')                             return { driver: 'formula', formula: 'retained_earnings' };
  if (role === 'ytd_net_income')                                        return { driver: 'formula', formula: 'net_income_ytd' };
  return null;
}

/* Build the full bs_assumptions row set for a classified meta blob.
 * total_clearing is a synthetic rollup (not a leaf) and needs its own
 * module_link row so the CFS end_cash injection resolves. */
export function buildBsAssumptions(bsActualsMeta) {
  const rows = [];
  const unmatched = [];
  for (const [id, m] of Object.entries(bsActualsMeta || {})) {
    if (!m || !m.role) continue; // synthetic label-override entries (e.g. total_clearing)
    const cfg = inferBsAssumption(m);
    if (!cfg) { unmatched.push({ id, role: m.role, subcategory: m.subcategory }); continue; }
    rows.push({ line_id: id, ...cfg });
  }
  if (unmatched.length) {
    throw new Error(
      '[buildBsAssumptions] UNMATCHED LINES — STOP: ' + JSON.stringify(unmatched)
    );
  }
  rows.push({ line_id: 'total_clearing', driver: 'module_link' });
  return rows;
}

/* -- W-013: THE RE-IMPORT PLAN -- a Step-4 re-import keeps every operator edit ----------------
 * Every Step-4 Import re-runs the seed. The server's upsert overwrites six columns and keeps only
 * `values`, so re-sending buildBsAssumptions whole reset every edit made on the Balance Sheet
 * "Show Assumptions" panel -- and half-reverted a per-month override ('constant' + a stranded map).
 * Brian's rule (09-25): a re-import seeds only rows that are NEW or UNTOUCHED; an edited row is
 * KEPT; an edited row whose line changed TYPE is re-seeded (its map cleared) and named; a row whose
 * line left the spreadsheet is deleted and named.
 *
 * EDIT vs SEED is decided by COMPARISON -- there is no provenance column, and updated_at cannot
 * serve (both writers stamp it; a clone copies it). A stored row equal to what the seed would have
 * written from the PRIOR meta is UNTOUCHED; one that differs was EDITED. The PRIOR meta is the one
 * still stored when the import handler runs, so the caller reads it BEFORE the import writes.
 *
 * bsAssumptionCanon mirrors the server upsert's own param expressions (server.js PUT
 * /bs-assumptions/bulk), so "what the seed would have written" means what the SERVER stores. It
 * accepts a GET /bs-assumptions entry (constantValue, empty values omitted) or a raw DB row
 * (constant_value, numeric strings); {} and NULL values are the same answer.
 *
 * planBsAssumptionSeed(priorMeta, newMeta, storedRows) -> {
 *   writes:    [{ line_id, updates }]   the rows to send to PUT /bs-assumptions/bulk, in
 *                                       buildBsAssumptions(newMeta) order
 *   seeded:    [line_id]                NEW, or UNTOUCHED -- seeded from the new meta
 *   kept:      [line_id]                EDITED, type unchanged -- NOT sent at all
 *   reseeded:  [{ line_id, label }]     EDITED and the type changed -- re-seeded, map cleared
 *   deleted:   [{ line_id, label }]     a stored row whose line is not in the new meta
 * }
 * storedRows is keyed by line_id (the GET's map). Throws exactly as buildBsAssumptions does on an
 * unmatched line in the NEW meta. With no prior meta and no stored rows (a first import) `writes`
 * is buildBsAssumptions(newMeta) wrapped as the import has always sent it -- byte-identical. */
export function bsAssumptionCanon(r) {
  const x = r || {};
  const cv = x.constantValue != null ? x.constantValue : (x.constant_value != null ? x.constant_value : null);
  const v = x.values && typeof x.values === 'object' && Object.keys(x.values).length ? x.values : null;
  return {
    driver: x.driver || 'constant',
    days: x.days != null ? Number(x.days) : null,
    base_is_line_id: x.base_is_line_id || null,
    formula: x.formula || null,
    fz: x.fz === true,
    constant_value: cv != null ? Number(cv) : null,
    values: v ? Object.keys(v).sort().map(k => [k, Number(v[k])]) : null,
  };
}

const bsaSame = (a, b) => JSON.stringify(bsAssumptionCanon(a)) === JSON.stringify(bsAssumptionCanon(b));
const bsaLabel = (id, ...metas) => {
  for (const m of metas) { const e = m && m[id]; if (e && (e.label || e.gl_name)) return e.label || e.gl_name; }
  return id;
};

export function planBsAssumptionSeed(priorMeta, newMeta, storedRows) {
  const prior = priorMeta || {};
  const stored = storedRows || {};
  const newRows = buildBsAssumptions(newMeta);
  // What the seed would have written from the PRIOR meta, per line. Built leaf by leaf (not through
  // buildBsAssumptions) so one prior leaf the current inference cannot match is simply "no prior
  // seed" -- its stored row then reads as EDITED and is kept, never silently reset.
  const priorSeed = {};
  for (const [id, m] of Object.entries(prior)) {
    if (!m || !m.role) continue;
    const cfg = inferBsAssumption(m);
    if (cfg) priorSeed[id] = cfg;
  }
  if (Object.keys(prior).length) priorSeed.total_clearing = { driver: 'module_link' };

  const writes = [], seeded = [], kept = [], reseeded = [];
  const newIds = new Set();
  for (const { line_id, ...u } of newRows) {
    newIds.add(line_id);
    const row = stored[line_id];
    if (!row || !(line_id in priorSeed)) {
      // NEW (no stored row, or a line the prior meta did not seed). A stale stored map is cleared.
      writes.push({ line_id, updates: row && bsAssumptionCanon(row).values ? { ...u, values: {} } : u });
      seeded.push(line_id);
      continue;
    }
    if (bsaSame(row, priorSeed[line_id])) {
      // UNTOUCHED -- equal to its seed, so its values are empty by construction: nothing to clear.
      writes.push({ line_id, updates: u });
      seeded.push(line_id);
      continue;
    }
    const p = prior[line_id], n = (newMeta || {})[line_id];
    const typeChanged = !!(p && n && (p.role !== n.role || (p.subcategory || null) !== (n.subcategory || null)));
    if (!typeChanged) { kept.push(line_id); continue; }
    // EDITED, and the line changed TYPE: the old setting belongs to the old type. Re-seed, and send
    // values: {} -- non-null, so the upsert's COALESCE clears the old map instead of keeping it.
    writes.push({ line_id, updates: { ...u, values: {} } });
    reseeded.push({ line_id, label: bsaLabel(line_id, newMeta, prior) });
  }
  const deleted = Object.keys(stored)
    .filter(id => !newIds.has(id))
    .map(id => ({ line_id: id, label: bsaLabel(id, prior, newMeta) }));
  return { writes, seeded, kept, reseeded, deleted };
}
